// src/components/ManualRegistrationModal.tsx
import React, { useState, useEffect } from "react";
import {
  createFreeRegistrationBatch,
  isAthleteRegisteredForCompetition,
} from "../firebase/registrations";
import { getAllClubs } from "../firebase/clubs";
import { getCoachUsers } from "../firebase/users";
import { useAthleteSearch, Athlete } from "../hooks/useAthleteSearch";
import { validateAthleteAge } from "../utils/ageValidator";
import { useAuth } from "../context/AuthContext";
import { categoryDisplayLabel } from "../utils/rankingCalculator";
import { getRecommendedWeights } from "../utils/weightSuggestions";
import WeightSelect from "./WeightSelect";
import { getFunctions, httpsCallable } from "firebase/functions";
import { C, FONT } from "../theme";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Coach {
  uid: string;
  email: string;
  displayName: string;
  club?: string;
}

interface BasketItem {
  athlete: Athlete;
  club: string;
  division: "Male" | "Female";
  category: string;
  weightCategory: string;
  price: number; // actual bank transfer price for this athlete
}

interface Competition {
  id: string;
  name: string;
  types: string[];
  weightCategories: { male: string[]; female: string[] };
  startDate?: any;
}

interface Props {
  competition: Competition;
  onClose: () => void;
  onSuccess: (referenceCode: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function fetchCoaches(): Promise<Coach[]> {
  // Cached, shared across admin pages — see firebase/users.ts
  const users = await getCoachUsers();
  return users.map((u) => ({
    uid: u.uid,
    email: u.email,
    displayName: u.displayName,
    club: u.club,
  }));
}

/**
 * Builds the HTML body for the admin manual registration confirmation email.
 * Bilingual EN/KA with federation logo (Phase 16).
 */
function buildManualConfirmationEmailHtml(params: {
  coachEmail: string;
  competitionName: string;
  referenceCode: string;
  athletes: BasketItem[];
  basketTotal: number;
}): string {
  const { competitionName, referenceCode, athletes, basketTotal } = params;

  const athleteRows = athletes
    .map(
      (item, i) => `
      <tr style="background:${i % 2 === 0 ? "#f8fafc" : "#ffffff"}">
        <td style="padding:10px 14px;font-size:13px;color:#0f172a;font-weight:600;">${item.athlete.firstName} ${item.athlete.lastName}</td>
        <td style="padding:10px 14px;font-size:13px;color:#475569;">${item.club}</td>
        <td style="padding:10px 14px;font-size:13px;color:#475569;">${categoryDisplayLabel(item.category)}</td>
        <td style="padding:10px 14px;font-size:13px;color:#475569;">${item.division}</td>
        <td style="padding:10px 14px;font-size:13px;color:#475569;">${item.weightCategory}</td>
        <td style="padding:10px 14px;font-size:13px;color:#0f172a;font-weight:700;text-align:right;">${item.price > 0 ? `${item.price} ₾` : "—"}</td>
      </tr>`
    )
    .join("");

  return `
<!DOCTYPE html>
<html lang="ka">
<head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
<body style="margin:0;padding:0;background-color:#f1f5f9;font-family:'Segoe UI',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f1f5f9;padding:32px 16px;">
    <tr><td align="center">
      <table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);border:1px solid #e2e8f0;">

        <!-- HEADER -->
        <tr>
          <td style="background:linear-gradient(135deg,#da291c 0%,var(--color-danger) 100%);padding:32px 32px 24px;text-align:center;">
            <img src="https://registration.taekwondo.ge/geo.png"
                 alt="Georgian Taekwondo Federation"
                 width="72" height="72"
                 style="width:72px;height:72px;object-fit:contain;display:block;margin:0 auto 14px;opacity:0.95;" />
            <div style="color:#ffffff;font-size:20px;font-weight:800;letter-spacing:2px;text-transform:uppercase;margin-bottom:4px;">GEORGIAN TAEKWONDO</div>
            <div style="color:rgba(255,255,255,0.70);font-size:12px;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:8px;">საქართველოს ტაეკვონდოს ფედერაცია</div>
            <div style="color:rgba(255,255,255,0.85);font-size:13px;letter-spacing:0.5px;">Registration Confirmation / რეგისტრაციის დადასტურება</div>
          </td>
        </tr>

        <!-- BADGE -->
        <tr>
          <td style="padding:24px 32px 0;text-align:center;">
            <div style="display:inline-block;background:var(--color-success-soft);border:1px solid #6ee7b7;border-radius:24px;padding:8px 20px;font-size:13px;font-weight:700;color:var(--color-success);letter-spacing:0.5px;">
              ✓ &nbsp;Registration Confirmed by Admin / ადმინის მიერ რეგისტრაცია დადასტურდა
            </div>
          </td>
        </tr>

        <!-- COMPETITION + REFERENCE -->
        <tr>
          <td style="padding:20px 32px;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;">
              <tr>
                <td style="padding:16px 20px;">
                  <div style="font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">Competition / შეჯიბრება</div>
                  <div style="font-size:16px;font-weight:700;color:#0f172a;">${competitionName}</div>
                </td>
                <td style="padding:16px 20px;text-align:right;border-left:1px solid #e2e8f0;">
                  <div style="font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">Reference Code / საცნობარო კოდი</div>
                  <div style="font-size:16px;font-weight:800;color:#da291c;letter-spacing:1px;">${referenceCode}</div>
                </td>
              </tr>
            </table>
          </td>
        </tr>

        <!-- ATHLETES TABLE -->
        <tr>
          <td style="padding:0 32px 8px;">
            <div style="font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">
              Registered Athletes (${athletes.length}) / რეგისტრირებული სპორტსმენები (${athletes.length})
            </div>
            <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;">
              <thead>
                <tr style="background:#f1f5f9;">
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Athlete / სპორტსმენი</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Club / კლუბი</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Category / კატეგორია</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Division / სქესი</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Weight / წონა</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:right;">Paid / გადახდილი</th>
                </tr>
              </thead>
              <tbody>
                ${athleteRows}
              </tbody>
            </table>
          </td>
        </tr>

        <!-- TOTAL (only if > 0) -->
        ${
          basketTotal > 0
            ? `<tr>
          <td style="padding:12px 32px 20px;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;">
              <tr>
                <td style="padding:14px 20px;font-size:13px;font-weight:600;color:var(--color-warning);">Total Paid via Bank Transfer / სულ გადახდილია საბანკო გადარიცხვით</td>
                <td style="padding:14px 20px;font-size:22px;font-weight:800;color:#da291c;text-align:right;">${basketTotal} ₾</td>
              </tr>
            </table>
          </td>
        </tr>`
            : `<tr><td style="padding:0 32px 20px;"></td></tr>`
        }

        <!-- VIEW HISTORY CTA -->
        <tr>
          <td style="padding:0 32px 24px;">
            <div style="background:var(--color-info-soft);border:1px solid #bfdbfe;border-radius:10px;padding:16px 20px;">
              <div style="font-size:13px;font-weight:700;color:#1e40af;margin-bottom:8px;">📋 View Your Registrations / თქვენი რეგისტრაციების ნახვა</div>
              <div style="font-size:13px;color:#1e40af;line-height:1.8;">
                This registration was added by the federation admin. You can view your full history at any time.<br/>
                ეს რეგისტრაცია დაამატა ფედერაციის ადმინმა. თქვენი სრული ისტორია ნებისმიერ დროს ხელმისაწვდომია.
              </div>
              <div style="margin-top:14px;">
                <a href="https://registration.taekwondo.ge/history"
                   style="display:inline-block;background:#da291c;color:#ffffff;font-size:13px;font-weight:700;padding:10px 20px;border-radius:8px;text-decoration:none;letter-spacing:0.3px;">
                  View My Registration History / ჩემი რეგისტრაციების ნახვა →
                </a>
              </div>
            </div>
          </td>
        </tr>

        <!-- FOOTER -->
        <tr>
          <td style="background:#f8fafc;border-top:1px solid #e2e8f0;padding:20px 32px;text-align:center;">
            <div style="font-size:12px;color:#94a3b8;line-height:1.8;">
              This is an automated confirmation from the Georgian Taekwondo Federation.<br/>
              ეს არის ქართული ტაეკვონდოს ფედერაციის ავტომატური შეტყობინება.<br/>
              For questions / კითხვებისთვის:
              <a href="mailto:taekwondogeo@gmail.com" style="color:#da291c;text-decoration:none;font-weight:600;">taekwondogeo@gmail.com</a>
            </div>
            <div style="margin-top:10px;">
              <a href="https://taekwondo.ge" style="font-size:12px;color:#94a3b8;text-decoration:none;">taekwondo.ge</a>
              &nbsp;·&nbsp;
              <a href="https://registration.taekwondo.ge" style="font-size:12px;color:#94a3b8;text-decoration:none;">registration.taekwondo.ge</a>
            </div>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ManualRegistrationModal({
  competition,
  onClose,
  onSuccess,
}: Props) {
  const { currentUser } = useAuth();

  const {
    filtered: athleteResults,
    search: athleteSearch,
    setSearch: setAthleteSearch,
    loading: athletesLoading,
  } = useAthleteSearch();

  const [clubs, setClubs] = useState<{ id: string; name: string }[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [loadingCoaches, setLoadingCoaches] = useState(true);
  const [selectedCoachId, setSelectedCoachId] = useState("");
  const [selectedCoachEmail, setSelectedCoachEmail] = useState("");

  const [selectedAthlete, setSelectedAthlete] = useState<Athlete | null>(null);
  const [club, setClub] = useState("");
  const [division, setDivision] = useState<"Male" | "Female">("Male");
  const [category, setCategory] = useState("");
  const [weightCategory, setWeightCategory] = useState("");
  const [athletePrice, setAthletePrice] = useState<string>("0");
  const [basket, setBasket] = useState<BasketItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [clubFocused, setClubFocused] = useState(false);

  const availableWeights =
    division === "Male"
      ? competition.weightCategories.male
      : competition.weightCategories.female;

  const filteredClubs = club
    ? clubs.filter((c) => c.name.toLowerCase().includes(club.toLowerCase()))
    : clubs;

  useEffect(() => {
    Promise.all([getAllClubs(), fetchCoaches()])
      .then(([clubList, coachList]) => {
        setClubs(clubList);
        setCoaches(coachList);
      })
      .catch((err) => console.error("Failed to load data:", err))
      .finally(() => {
        setLoadingClubs(false);
        setLoadingCoaches(false);
      });
  }, []);

  useEffect(() => {
    const available =
      division === "Male"
        ? competition.weightCategories.male
        : competition.weightCategories.female;
    if (!available.includes(weightCategory)) {
      setWeightCategory(available[0] ?? "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [division, competition.weightCategories]);

  useEffect(() => {
    if (competition.types.length === 1 && !category) {
      setCategory(competition.types[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition.types]);

  // When coach selection changes, store their email too
  const handleCoachChange = (uid: string) => {
    setSelectedCoachId(uid);
    if (uid === "admin") {
      setSelectedCoachEmail(currentUser?.email ?? "admin");
    } else {
      const coach = coaches.find((c) => c.uid === uid);
      setSelectedCoachEmail(coach?.email ?? "");
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleSelectAthlete = (athlete: Athlete) => {
    setSelectedAthlete(athlete);
    setClub(athlete.club ?? "");
    setAthleteSearch(`${athlete.firstName} ${athlete.lastName}`);
  };

  const handleAddToBasket = async () => {
    setError("");

    if (!selectedCoachId) {
      setError("Please select a coach or 'Admin (No Coach)'.");
      return;
    }
    if (!selectedAthlete) {
      setError("Please search and select an athlete.");
      return;
    }
    if (!club.trim()) {
      setError("Club is required.");
      return;
    }
    if (!category) {
      setError("Category is required.");
      return;
    }
    if (!weightCategory) {
      setError("Weight category is required.");
      return;
    }

    const parsedPrice = parseFloat(athletePrice) || 0;

    // Age validation
    if (competition.startDate) {
      const competitionYear = competition.startDate.toDate
        ? competition.startDate.toDate().getFullYear()
        : new Date(competition.startDate).getFullYear();
      const ageCheck = validateAthleteAge(
        selectedAthlete.dateOfBirth,
        category,
        competitionYear,
        `${selectedAthlete.firstName} ${selectedAthlete.lastName}`
      );
      if (ageCheck.warning) {
        if (!window.confirm(ageCheck.warning)) return;
      }
    }

    const alreadyInBasket = basket.some(
      (b) => b.athlete.id === selectedAthlete.id
    );
    if (alreadyInBasket) {
      setError("This athlete is already in the basket.");
      return;
    }

    setChecking(true);
    try {
      const alreadyRegistered = await isAthleteRegisteredForCompetition(
        selectedAthlete.athleteId ?? selectedAthlete.id,
        competition.id
      );
      if (alreadyRegistered) {
        setError(
          `${selectedAthlete.firstName} ${selectedAthlete.lastName} is already registered for this competition.`
        );
        return;
      }
    } catch (err) {
      console.error("Duplicate check failed:", err);
    } finally {
      setChecking(false);
    }

    setBasket((prev) => [
      ...prev,
      {
        athlete: selectedAthlete,
        club: club.trim(),
        division,
        category,
        weightCategory,
        price: parsedPrice,
      },
    ]);

    setSelectedAthlete(null);
    setAthleteSearch("");
    setClub("");
    setAthletePrice("0");
    setCategory(competition.types.length === 1 ? competition.types[0] : "");
    setWeightCategory(availableWeights[0] ?? "");
  };

  const removeFromBasket = (athleteId: string) => {
    setBasket((prev) => prev.filter((b) => b.athlete.id !== athleteId));
  };

  // ── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError("");
    if (!selectedCoachId) {
      setError("Please select a coach.");
      return;
    }
    if (basket.length === 0) {
      setError("Add at least one athlete to the basket.");
      return;
    }

    setSubmitting(true);
    try {
      const coachIdToUse =
        selectedCoachId === "admin"
          ? currentUser?.uid || "admin"
          : selectedCoachId;

      // Denormalized coach name for the public registrations page.
      const selectedCoachName =
        selectedCoachId === "admin"
          ? currentUser?.displayName ?? "Admin"
          : coaches.find((c) => c.uid === selectedCoachId)?.displayName ?? "";

      const referenceCode = await createFreeRegistrationBatch(
        coachIdToUse,
        competition.id,
        competition.name,
        basket.map((b) => ({
          athleteId: b.athlete.athleteId ?? b.athlete.id,
          athleteSnapshot: {
            firstName: b.athlete.firstName,
            lastName: b.athlete.lastName,
            dateOfBirth: b.athlete.dateOfBirth,
          },
          club: b.club,
          weightCategory: b.weightCategory,
          division: b.division,
          category: b.category,
          price: b.price,
        })),
        selectedCoachEmail,
        selectedCoachName
      );

      // Send confirmation email to coach — non-blocking, never prevents success
      // Only send if a real coach (not admin-only entry) and email is known
      if (selectedCoachEmail && selectedCoachEmail !== "admin") {
        try {
          const functions = getFunctions(undefined, "europe-central2");
          const sendEmail = httpsCallable(functions, "sendEmail");
          await sendEmail({
            to: selectedCoachEmail,
            subject: `Registration Confirmed by Admin / ადმინის მიერ დადასტურება — ${competition.name} [${referenceCode}]`,
            html: buildManualConfirmationEmailHtml({
              coachEmail: selectedCoachEmail,
              competitionName: competition.name,
              referenceCode,
              athletes: basket,
              basketTotal: basket.reduce((s, b) => s + b.price, 0),
            }),
          });
        } catch (emailErr) {
          // Email failure is logged but does NOT block the success flow
          console.error("Confirmation email failed (non-blocking):", emailErr);
        }
      }

      onSuccess(referenceCode);
    } catch (err) {
      console.error("Manual registration failed:", err);
      setError("Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const basketTotal = basket.reduce((s, b) => s + b.price, 0);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={s.label}>MANUAL REGISTRATION</div>
            <div style={s.title}>{competition.name}</div>
            <div style={s.subtitle}>
              Bank transfer / offline-payment registration
            </div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div style={s.body}>

          {/* Step 1: Select Coach */}
          <div style={s.section}>
            <div style={s.sectionTitle}>1 · Select Coach</div>
            {loadingCoaches ? (
              <div style={s.hint}>Loading coaches…</div>
            ) : (
              <select
                style={s.select}
                value={selectedCoachId}
                onChange={(e) => handleCoachChange(e.target.value)}
              >
                <option value="">— Select coach —</option>
                <option value="admin">Admin (No Coach / Direct)</option>
                {coaches.map((c) => (
                  <option key={c.uid} value={c.uid}>
                    {c.displayName} {c.email ? `(${c.email})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div style={s.divider} />

          {/* Step 2: Add Athlete */}
          <div style={s.section}>
            <div style={s.sectionTitle}>2 · Add Athlete to Basket</div>

            {/* Athlete search */}
            <div style={{ position: "relative" }}>
              <div style={s.fieldLabel}>Athlete</div>
              <input
                style={s.input}
                placeholder="Type name or ID to search…"
                value={athleteSearch}
                onChange={(e) => {
                  setAthleteSearch(e.target.value);
                  setSelectedAthlete(null);
                }}
                autoComplete="off"
              />
              {athleteSearch.length >= 2 && !selectedAthlete && (
                <div style={s.dropdown}>
                  {athletesLoading ? (
                    <div style={{ padding: "12px 14px", color: C.text3, fontSize: "13px" }}>
                      Searching…
                    </div>
                  ) : athleteResults.length === 0 ? (
                    <div style={{ padding: "12px 14px", color: C.text3, fontSize: "13px" }}>
                      No athletes found.
                    </div>
                  ) : (
                    athleteResults.slice(0, 8).map((a) => (
                      <div
                        key={a.id}
                        style={s.dropdownItem}
                        onClick={() => handleSelectAthlete(a)}
                      >
                        <div style={s.dropdownName}>
                          {a.firstName} {a.lastName}
                        </div>
                        <div style={s.dropdownMeta}>
                          DOB: {a.dateOfBirth} · {a.club || "No club"}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              {selectedAthlete && (
                <div style={s.selectedBadge}>
                  ✓ {selectedAthlete.firstName} {selectedAthlete.lastName} selected
                </div>
              )}
            </div>

            {/* Club, Division, Category, Weight, Price */}
            <div style={s.fieldGrid}>
              <div style={s.field}>
                <div style={s.fieldLabel}>Club</div>
                {loadingClubs ? (
                  <div style={s.hint}>Loading…</div>
                ) : (
                  <div style={{ position: "relative" }}>
                    <input
                      style={s.input}
                      type="text"
                      value={club}
                      onChange={(e) => setClub(e.target.value)}
                      onFocus={() => setClubFocused(true)}
                      onBlur={() => setTimeout(() => setClubFocused(false), 150)}
                      placeholder="Type or select club…"
                      autoComplete="off"
                    />
                    {clubFocused && filteredClubs.length > 0 && (
                      <div style={s.dropdown}>
                        {filteredClubs.slice(0, 10).map((c) => (
                          <div
                            key={c.id}
                            style={s.dropdownItem}
                            onMouseDown={() => setClub(c.name)}
                          >
                            <div style={s.dropdownName}>{c.name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={s.field}>
                <div style={s.fieldLabel}>Division</div>
                <select
                  style={s.select}
                  value={division}
                  onChange={(e) => setDivision(e.target.value as "Male" | "Female")}
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              <div style={s.field}>
                <div style={s.fieldLabel}>Category</div>
                {competition.types.length === 1 ? (
                  <div style={{ ...s.input, color: C.text3 }}>{categoryDisplayLabel(competition.types[0])}</div>
                ) : (
                  <select
                    style={s.select}
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="">— Select —</option>
                    {competition.types.map((tp) => (
                      <option key={tp} value={tp}>{categoryDisplayLabel(tp)}</option>
                    ))}
                  </select>
                )}
              </div>

              <div style={s.field}>
                <div style={s.fieldLabel}>Weight Category</div>
                <WeightSelect
                  value={weightCategory}
                  onChange={setWeightCategory}
                  weights={availableWeights}
                  recommended={getRecommendedWeights(category, division)}
                  category={category}
                  division={division}
                  placeholder="— Select —"
                  selectStyle={s.select}
                />
              </div>

              {/* Price field */}
              <div style={{ ...s.field, gridColumn: "1 / -1" }}>
                <div style={s.fieldLabel}>Amount Paid by Bank (GEL)</div>
                <input
                  style={s.input}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={athletePrice}
                  onChange={(e) => setAthletePrice(e.target.value)}
                />
                <div style={s.hint}>Enter 0 if not yet paid or paid amount is unknown.</div>
              </div>
            </div>

            {error && <div style={s.error}>{error}</div>}

            <button
              style={checking ? s.addBtnDisabled : s.addBtn}
              onClick={handleAddToBasket}
              disabled={checking}
            >
              {checking ? "Checking…" : "+ Add to Basket"}
            </button>
          </div>

          {/* Basket */}
          {basket.length > 0 && (
            <>
              <div style={s.divider} />
              <div style={s.section}>
                <div style={s.sectionTitle}>
                  Basket — {basket.length} athlete{basket.length !== 1 ? "s" : ""}
                  {basketTotal > 0 && (
                    <span style={s.basketTotalBadge}> · {basketTotal} GEL total</span>
                  )}
                </div>
                <div style={s.basketList}>
                  {basket.map((item) => (
                    <div key={item.athlete.id} style={s.basketItem}>
                      <div style={s.basketInfo}>
                        <div style={s.basketName}>
                          {item.athlete.firstName} {item.athlete.lastName}
                        </div>
                        <div style={s.basketMeta}>
                          {categoryDisplayLabel(item.category)} · {item.division} · {item.weightCategory} · {item.club}
                          {item.price > 0 && (
                            <span style={s.priceBadge}> {item.price} GEL</span>
                          )}
                        </div>
                      </div>
                      <button
                        style={s.removeBtn}
                        onClick={() => removeFromBasket(item.athlete.id)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div style={s.divider} />

          {/* Submit */}
          <div style={s.actions}>
            <button style={s.cancelBtn} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              style={
                submitting || basket.length === 0 || !selectedCoachId
                  ? s.submitBtnDisabled
                  : s.submitBtn
              }
              onClick={handleSubmit}
              disabled={submitting || basket.length === 0 || !selectedCoachId}
            >
              {submitting
                ? "Registering…"
                : `Register ${basket.length} Athlete${basket.length !== 1 ? "s" : ""}${basketTotal > 0 ? ` · ${basketTotal} GEL` : " (Free)"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modal: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "14px",
    width: "100%",
    maxWidth: "640px",
    maxHeight: "92vh",
    overflowY: "auto",
    boxShadow: "0 28px 80px rgba(0,0,0,0.35)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "22px 24px 16px",
    borderBottom: `1px solid ${C.border}`,
    position: "sticky",
    top: 0,
    backgroundColor: C.surface,
    zIndex: 1,
  },
  label: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "4px",
  },
  title: {
    fontFamily: FONT.display,
    fontSize: "22px",
    color: C.text,
    letterSpacing: "0.5px",
    lineHeight: 1.1,
    marginBottom: "4px",
  },
  subtitle: { fontSize: "12px", color: C.text3 },
  closeBtn: {
    background: "none",
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    color: C.text3,
    fontSize: "16px",
    cursor: "pointer",
    padding: "4px 8px",
    lineHeight: 1,
    flexShrink: 0,
  },
  body: { padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: "0" },
  section: { display: "flex", flexDirection: "column", gap: "12px" },
  sectionTitle: {
    fontSize: "11px",
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginBottom: "2px",
  },
  divider: { height: "1px", backgroundColor: C.border, margin: "18px 0" },
  hint: { fontSize: "12px", color: C.text3 },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
  },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  fieldLabel: {
    fontSize: "10px",
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  input: {
    padding: "11px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "16px",
    color: C.text,
    backgroundColor: C.surface2,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    padding: "11px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "16px",
    color: C.text,
    backgroundColor: C.surface2,
    outline: "none",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    zIndex: 10,
    maxHeight: "min(300px, 50vh)",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch" as any,
  },
  dropdownItem: {
    padding: "12px 14px",
    minHeight: "44px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "2px",
    borderBottom: `1px solid ${C.border}`,
  },
  dropdownName: { fontSize: "13px", fontWeight: 600, color: C.text },
  dropdownMeta: { fontSize: "11px", color: C.text3 },
  selectedBadge: { marginTop: "4px", fontSize: "12px", color: C.success, fontWeight: 600 },
  error: {
    backgroundColor: C.redDim,
    border: `1px solid rgba(218,41,28,0.3)`,
    borderRadius: "7px",
    padding: "10px 14px",
    fontSize: "13px",
    color: C.red,
  },
  addBtn: {
    alignSelf: "flex-start",
    backgroundColor: C.surface2,
    color: C.text,
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
  },
  addBtnDisabled: {
    alignSelf: "flex-start",
    backgroundColor: C.border,
    color: C.text3,
    border: `1px solid ${C.border}`,
    borderRadius: "7px",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "not-allowed",
  },
  basketTotalBadge: {
    color: C.red,
    fontWeight: 700,
    textTransform: "none",
    letterSpacing: "0",
  },
  basketList: { display: "flex", flexDirection: "column", gap: "8px" },
  basketItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    padding: "10px 14px",
  },
  basketInfo: { display: "flex", flexDirection: "column", gap: "2px" },
  basketName: { fontSize: "13px", fontWeight: 600, color: C.text },
  basketMeta: { fontSize: "11px", color: C.text3 },
  priceBadge: {
    display: "inline-block",
    marginLeft: "4px",
    color: C.red,
    fontWeight: 700,
  },
  removeBtn: {
    background: "none",
    border: "none",
    color: C.text3,
    fontSize: "14px",
    cursor: "pointer",
    padding: "4px 6px",
    borderRadius: "4px",
  },
  actions: { display: "flex", gap: "10px", justifyContent: "flex-end" },
  cancelBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    padding: "10px 20px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 500,
  },
  submitBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  submitBtnDisabled: {
    backgroundColor: C.border,
    color: C.text3,
    border: "none",
    borderRadius: "7px",
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "not-allowed",
  },
};